import { useState, useRef, useEffect } from "react";
import { Pencil, Trash2, Package, PackageOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { EquipmentAutocomplete } from "./EquipmentAutocomplete";
import { InstanceTagsInput } from "./InstanceTagsInput";
import { ConflictBadge } from "./ConflictBadge";
import { EquipmentConflictDialog } from "./EquipmentConflictDialog";
import type { ProjectEquipmentAssignment } from "@/hooks/useProjectEquipment";
import type { ConflictSummary } from "@/hooks/useEquipmentConflicts";
import type { EquipmentInstance } from "@/hooks/useEquipmentInstances";
import { useIsMobile } from "@/hooks/use-mobile";
import { formatEquipmentName } from "@/lib/equipment-utils";
import { useDraftField } from "@/hooks/useDraftField";

interface EquipmentAssignmentRowProps {
  assignment: ProjectEquipmentAssignment;
  isNew?: boolean;
  conflict?: ConflictSummary;
  instances?: EquipmentInstance[];
  currentProjectId: string;
  onNewHandled?: () => void;
  onUpdate: (updates: {
    amount?: number;
    equipment_id?: string | null;
    temporary_equipment_id?: string | null;
  }) => void;
  onEdit: () => void;
  onDelete: () => void;
  onCreateTemporary: (name: string) => Promise<string>;
  onUpdateTemporary: (id: string, name: string) => void;
  onAssignInstance: (instanceId: string) => void;
  onUnassignInstance: (assignmentInstanceId: string) => void;
}

export function EquipmentAssignmentRow({
  assignment,
  isNew = false,
  conflict,
  instances: instancesProp,
  currentProjectId,
  onNewHandled,
  onUpdate,
  onEdit,
  onDelete,
  onCreateTemporary,
  onUpdateTemporary,
  onAssignInstance,
  onUnassignInstance,
}: EquipmentAssignmentRowProps) {
  const isMobile = useIsMobile();
  const [isEditingAmount, setIsEditingAmount] = useState(!isMobile && isNew);
  const [isEditingEquipment, setIsEditingEquipment] = useState(
    !isMobile && isNew,
  );
  const [amount, setAmount] = useState(assignment.amount);
  const [isConflictDialogOpen, setIsConflictDialogOpen] = useState(false);
  const amountInputRef = useRef<HTMLInputElement>(null);

  const equipmentName = assignment.equipment
    ? formatEquipmentName(
        assignment.equipment.name,
        assignment.equipment.manufacturer,
      )
    : assignment.temporary_equipment?.name || "";
  const isTemporary = !!assignment.temporary_equipment_id;
  const isSerialized = assignment.equipment?.is_serialized || false;
  const instances = instancesProp || [];
  const temporaryNameDraft = useDraftField<string>({
    value: assignment.temporary_equipment?.name ?? "",
    toShared: (draft) => draft.trim(),
    onCommit: (name) => {
      if (
        assignment.temporary_equipment_id &&
        name &&
        name !== assignment.temporary_equipment?.name
      ) {
        onUpdateTemporary(assignment.temporary_equipment_id, name);
      }
      setIsEditingEquipment(false);
    },
  });

  const assignedInstanceIds = new Set(
    assignment.instance_assignments?.map((ia) => ia.instance_id) || [],
  );

  const availableInstances = instances.filter(
    (inst) => !assignedInstanceIds.has(inst.id) && inst.status === "active",
  );

  useEffect(() => {
    if (isEditingAmount && amountInputRef.current) {
      amountInputRef.current.focus();
      amountInputRef.current.select();
    }
  }, [isEditingAmount]);

  useEffect(() => {
    if (isNew && !isMobile) {
      setIsEditingAmount(true);
      setIsEditingEquipment(true);
      onNewHandled?.();
    }
  }, [isMobile, isNew, onNewHandled]);

  useEffect(() => {
    if (!isEditingAmount) {
      setAmount(assignment.amount);
    }
  }, [assignment.amount, isEditingAmount]);

  const handleAmountBlur = () => {
    setIsEditingAmount(false);
    if (amount !== assignment.amount) {
      onUpdate({ amount });
    }
  };

  const handleAmountKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleAmountBlur();
    }
    if (e.key === "Escape") {
      setAmount(assignment.amount);
      setIsEditingAmount(false);
    }
  };

  const handleEquipmentChange = async (
    value: { id: string; name: string; isTemporary: boolean } | null,
  ) => {
    if (value && !value.isTemporary) {
      // Selecting normal equipment - clear temporary_equipment_id
      onUpdate({ equipment_id: value.id, temporary_equipment_id: null });
      setIsEditingEquipment(false);
    }
    // If value is null or isTemporary, don't close - wait for user to continue typing or select
  };

  const handleCreateTemporary = async (name: string) => {
    const tempId = await onCreateTemporary(name);
    onUpdate({ temporary_equipment_id: tempId, equipment_id: null });
    setIsEditingEquipment(false);
  };

  const handleTemporaryNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void temporaryNameDraft.commit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      temporaryNameDraft.cancel();
      setIsEditingEquipment(false);
    }
  };

  const availableQuantity = conflict
    ? conflict.activeInstanceCount !== null
      ? conflict.activeInstanceCount
      : conflict.stockQuantity
    : null;

  // Build replacement handler for conflict dialog
  const handleReplaceInstance = (
    oldInstanceId: string,
    newInstanceId: string,
  ) => {
    // Find the instance assignment record for the old instance
    const oldAssignment = assignment.instance_assignments?.find(
      (ia) => ia.instance_id === oldInstanceId,
    );
    if (oldAssignment) {
      onUnassignInstance(oldAssignment.id);
    }
    onAssignInstance(newInstanceId);
  };

  // Available instances for replacement in dialog (active, not already assigned to this row)
  const dialogAvailableInstances = instances
    .filter(
      (inst) => !assignedInstanceIds.has(inst.id) && inst.status === "active",
    )
    .map((inst) => ({ id: inst.id, identifier: inst.identifier }));

  // Mobile view - table-like row with horizontal separator
  if (isMobile) {
    return (
      <>
        <div
          className="border-b border-border py-3 cursor-pointer"
          onClick={onEdit}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0">
              <span className="text-sm font-medium flex-shrink-0">
                {assignment.amount}x
              </span>
              {conflict && availableQuantity !== null && (
                <ConflictBadge
                  totalUsed={conflict.totalUsed}
                  availableQuantity={availableQuantity}
                  isSerialized={conflict.isSerialized}
                  hasConflict={conflict.hasConflict}
                  onClick={() => setIsConflictDialogOpen(true)}
                />
              )}
              {isTemporary ? (
                <PackageOpen className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              ) : (
                <Package className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
              )}
              <div className="flex min-w-0 flex-wrap items-center gap-1">
                <span className="text-sm">
                  {equipmentName || "Select equipment..."}
                </span>
                {isSerialized &&
                  assignment.instance_assignments &&
                  assignment.instance_assignments.length > 0 &&
                  assignment.instance_assignments.map((ia) => (
                    <Badge key={ia.id} variant="outline" className="text-xs">
                      {ia.instance?.identifier}
                    </Badge>
                  ))}
              </div>
            </div>
          </div>
        </div>
        {conflict && (
          <EquipmentConflictDialog
            open={isConflictDialogOpen}
            onClose={() => setIsConflictDialogOpen(false)}
            equipmentId={conflict.equipmentId}
            equipmentName={conflict.equipmentName}
            isSerialized={conflict.isSerialized}
            stockQuantity={conflict.stockQuantity}
            activeInstanceCount={conflict.activeInstanceCount}
            conflicts={conflict.conflicts}
            currentProjectId={currentProjectId}
            overlappingInstanceIds={conflict.overlappingInstanceIds}
            availableInstances={dialogAvailableInstances}
            onReplaceInstance={handleReplaceInstance}
          />
        )}
      </>
    );
  }

  // Desktop view - table row with inline editing
  return (
    <>
      <TableRow className="group">
        <TableCell className="w-20">
          {isEditingAmount ? (
            <Input
              ref={amountInputRef}
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(parseInt(e.target.value) || 1)}
              onBlur={handleAmountBlur}
              onKeyDown={handleAmountKeyDown}
              className="w-16 h-8"
            />
          ) : (
            <div
              className="cursor-pointer hover:bg-muted px-2 py-1 rounded"
              onClick={() => setIsEditingAmount(true)}
            >
              {assignment.amount}
            </div>
          )}
        </TableCell>

        <TableCell>
          {isEditingEquipment && isTemporary ? (
            <Input
              value={temporaryNameDraft.draft}
              onFocus={temporaryNameDraft.start}
              onChange={(e) => temporaryNameDraft.setDraft(e.target.value)}
              onBlur={() => void temporaryNameDraft.commit()}
              onKeyDown={handleTemporaryNameKeyDown}
              className="h-8"
              autoFocus
            />
          ) : isEditingEquipment ? (
            <EquipmentAutocomplete
              value={
                assignment.equipment_id
                  ? {
                      id: assignment.equipment_id,
                      name: equipmentName,
                      isTemporary: false,
                    }
                  : assignment.temporary_equipment_id
                    ? {
                        id: assignment.temporary_equipment_id,
                        name: equipmentName,
                        isTemporary: true,
                      }
                    : null
              }
              onChange={handleEquipmentChange}
              onCreateTemporary={handleCreateTemporary}
              autoFocus
            />
          ) : (
            <div
              className="cursor-pointer hover:bg-muted px-2 py-1 rounded flex items-center gap-2"
              onClick={() => setIsEditingEquipment(true)}
            >
              {isTemporary ? (
                <PackageOpen className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Package className="h-4 w-4 text-muted-foreground" />
              )}
              <span>{equipmentName || "Select equipment..."}</span>
              {isTemporary && (
                <Badge variant="secondary" className="text-xs">
                  Temporary
                </Badge>
              )}
            </div>
          )}
        </TableCell>

        <TableCell className="w-16">
          {conflict && availableQuantity !== null && (
            <ConflictBadge
              totalUsed={conflict.totalUsed}
              availableQuantity={availableQuantity}
              isSerialized={conflict.isSerialized}
              hasConflict={conflict.hasConflict}
              onClick={() => setIsConflictDialogOpen(true)}
            />
          )}
        </TableCell>

        <TableCell className="w-48">
          {isSerialized && (
            <InstanceTagsInput
              assignedInstances={assignment.instance_assignments || []}
              availableInstances={availableInstances}
              onAssign={onAssignInstance}
              onUnassign={onUnassignInstance}
              maxInstances={assignment.amount}
              compact
            />
          )}
        </TableCell>

        <TableCell className="w-24 text-right">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onEdit}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={onDelete}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
      {conflict && (
        <EquipmentConflictDialog
          open={isConflictDialogOpen}
          onClose={() => setIsConflictDialogOpen(false)}
          equipmentId={conflict.equipmentId}
          equipmentName={conflict.equipmentName}
          isSerialized={conflict.isSerialized}
          stockQuantity={conflict.stockQuantity}
          activeInstanceCount={conflict.activeInstanceCount}
          conflicts={conflict.conflicts}
          currentProjectId={currentProjectId}
          overlappingInstanceIds={conflict.overlappingInstanceIds}
          availableInstances={dialogAvailableInstances}
          onReplaceInstance={handleReplaceInstance}
        />
      )}
    </>
  );
}
